import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { ArrowDown, Download, EllipsisVertical, Share, SquarePlus, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { RuStoreBadge } from '@/components/ui/RuStoreBadge'
import { storage } from '@/lib/platform/storage'
import {
  consumeInstallEvent,
  getInstallEvent,
  subscribeInstallEvent,
  type BeforeInstallPromptEvent,
} from '@/lib/pwaInstall'

/**
 * Предложение установить приложение.
 *
 * Три несводимые ветки, и выбор между ними — не про красоту, а про то,
 * что в этом браузере вообще возможно.
 *
 *  1. Chrome и родня на десктопе и Android: есть beforeinstallprompt,
 *     установка в один тап нашей кнопкой.
 *  2. Safari на iOS: события нет и не будет, единственный путь —
 *     «Поделиться → На экран „Домой“».
 *  3. Мобильные Chromium-сборки, где события так и не случилось —
 *     Яндекс.Браузер его не шлёт вовсе, а Chrome на Android тянет
 *     с ним, пока не наберётся достаточное взаимодействие со страницей.
 *     Раньше такой пользователь не видел ничего: код считал, что «не iOS»
 *     значит «событие будет». Теперь, подождав, показываем путь через меню.
 *
 * Аудитория преимущественно 40+, поэтому подсказки нарисованы,
 * а не описаны словами: иначе пункт меню просто не находят.
 *
 * С 21.09.2026 на Android вместо установки сайта предлагается настоящее
 * приложение из RuStore — четвёртая ветка, и она важнее трёх остальных:
 * в приложении есть напоминания, голосовые заметки и полноценный офлайн.
 * Предлагаем его и тем, кто уже поставил сайт на главный экран.
 */

/** Приложение установлено — предлагать больше нечего, это навсегда. */
const INSTALLED_KEY = 'lightchat_pwa_installed'
const DISMISSED_KEY = 'lightchat_pwa_install_dismissed'
const IOS_DISMISSED_KEY = 'lightchat_pwa_ios_hint_dismissed'
const RUSTORE_DISMISSED_KEY = 'lightchat_rustore_hint_dismissed'

/**
 * Через сколько предложить снова после закрытия крестиком.
 *
 * Раньше крестик означал «никогда»: одно случайное касание — и человек,
 * который через месяц захотел бы приложение, уже никак об этом не узнавал.
 */
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Сколько ждать beforeinstallprompt, прежде чем объяснять путь руками.
 *
 * Достаточно долго, чтобы не мигать подсказкой там, где событие просто
 * задержалось, и достаточно коротко, чтобы застать человека на странице.
 */
const PROMPT_WAIT_MS = 12_000

type PromptMode = 'oneTap' | 'ios' | 'menu' | 'rustore'

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari до сих пор не поддерживает display-mode и держит свой флаг
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent
  const isIos =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ по умолчанию представляется десктопным Mac,
    // отличить можно только по наличию тач-точек
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
  if (!isIos) return false

  // Все браузеры на iOS работают на WebKit, но пункт «На экран „Домой“»
  // есть только в Safari — в Chrome, Firefox и Edge подсказка соврёт
  return !/crios|fxios|edgios|opios/i.test(ua)
}

/** Android-браузер: подсказка про меню осмысленна только на телефоне. */
function isAndroid(): boolean {
  return /android/i.test(window.navigator.userAgent)
}

/**
 * Отложено ли предложение.
 *
 * В ключе лежит момент, до которого молчим. Значение '1' осталось
 * от прежней версии, где крестик значил «никогда»; считаем его истёкшим,
 * иначе тот, кто когда-то закрыл баннер, не увидит исправленного
 * предложения уже никогда.
 */
function isSnoozed(key: string): boolean {
  const raw = storage.get(key)
  if (!raw || raw === '1') return false
  const until = Number(raw)
  return Number.isFinite(until) && Date.now() < until
}

function snooze(key: string) {
  storage.set(key, String(Date.now() + SNOOZE_MS))
}

/** Шаг подсказки: строка с иконкой, а не абзац текста. */
function Step({ children, icon }: { children: string; icon: ReactElement }) {
  return (
    <div className="flex items-center gap-3 rounded-card border border-divider bg-background px-3 py-2">
      {icon}
      <span className="flex-1 text-sm text-gray-700">{children}</span>
    </div>
  )
}

function StepArrow() {
  return (
    <div className="flex justify-center">
      <ArrowDown size={18} className="text-muted" />
    </div>
  )
}

/** Имитация двух шагов Safari — картинка, а не описание словами. */
function IosSteps() {
  return (
    <div className="mt-3 space-y-2" aria-hidden="true">
      <div className="flex items-center gap-3 rounded-card border border-divider bg-background px-3 py-2">
        <Share size={22} className="shrink-0 text-[#007AFF]" />
        <span className="text-sm text-gray-700">Кнопка «Поделиться» внизу браузера</span>
      </div>

      <StepArrow />

      <div className="flex items-center gap-3 rounded-card border border-divider bg-background px-3 py-2">
        <span className="flex-1 text-sm text-gray-700">На экран «Домой»</span>
        <SquarePlus size={22} className="shrink-0 text-gray-900" />
      </div>
    </div>
  )
}

/**
 * Путь через меню браузера. Формулировка нарочно с «или»: в Chrome пункт
 * называется «Установить приложение», в Яндекс.Браузере — «Добавить
 * на главный экран», и угадывать по строке user-agent, какой именно
 * увидит человек, надёжнее не выйдет.
 */
function MenuSteps() {
  return (
    <div className="mt-3 space-y-2" aria-hidden="true">
      <Step icon={<EllipsisVertical size={22} className="shrink-0 text-gray-900" />}>
        Меню браузера — три точки сверху
      </Step>

      <StepArrow />

      <Step icon={<Download size={22} className="shrink-0 text-gray-900" />}>
        «Установить приложение» или «Добавить на главный экран»
      </Step>
    </div>
  )
}

export function InstallPrompt() {
  const [mode, setMode] = useState<PromptMode | null>(null)
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    if (isAndroid()) {
      if (!isSnoozed(RUSTORE_DISMISSED_KEY)) setMode('rustore')
      return
    }

    if (isStandalone() || storage.get(INSTALLED_KEY) === '1') return

    if (isIosSafari()) {
      // beforeinstallprompt в Safari не наступит никогда — подписываться не на что
      if (!isSnoozed(IOS_DISMISSED_KEY)) setMode('ios')
      return
    }

    if (isSnoozed(DISMISSED_KEY)) return

    // Событие перехвачено в main.tsx до первого рендера — здесь оно только
    // забирается. Подписка зовёт слушателя сразу, поэтому уже пришедшее
    // событие не теряется, а ещё не пришедшее подхватится позже.
    const unsubscribe = subscribeInstallEvent((event) => {
      if (!event) return
      setInstallEvent(event)
      setMode('oneTap')
    })

    // Если события так и нет — на телефоне объясняем путь через меню.
    // На десктопе молчим: там установка редкость, а промахнуться
    // с названием пункта меню легче, чем помочь.
    const timer = window.setTimeout(() => {
      if (getInstallEvent() || !isAndroid()) return
      setMode((current) => current ?? 'menu')
    }, PROMPT_WAIT_MS)

    return () => {
      unsubscribe()
      window.clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    const onInstalled = () => {
      storage.set(INSTALLED_KEY, '1')
      setMode(null)
      setInstallEvent(null)
    }
    window.addEventListener('appinstalled', onInstalled)
    return () => window.removeEventListener('appinstalled', onInstalled)
  }, [])

  const install = useCallback(async () => {
    if (!installEvent) return
    await installEvent.prompt()
    const { outcome } = await installEvent.userChoice
    // Повторно показать это же событие нельзя — оно одноразовое
    consumeInstallEvent()
    setInstallEvent(null)
    setMode(null)
    if (outcome === 'accepted') storage.set(INSTALLED_KEY, '1')
    else snooze(DISMISSED_KEY)
  }, [installEvent])

  const dismiss = useCallback(() => {
    snooze(
      mode === 'ios' ? IOS_DISMISSED_KEY : mode === 'rustore' ? RUSTORE_DISMISSED_KEY : DISMISSED_KEY,
    )
    setMode(null)
    setInstallEvent(null)
  }, [mode])

  if (!mode) return null

  return (
    <div
      role="dialog"
      aria-label="Установка приложения"
      /*
       * Над мобильным таб-баром AppShell (56px + безопасная зона), иначе
       * баннер перекрывает навигацию. На десктопе — карточка в углу.
       */
      className="lc-no-print fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(3.5rem+env(safe-area-inset-bottom)+0.75rem)] md:inset-x-auto md:right-4 md:bottom-4 md:px-0 md:pb-0"
    >
      <div className="mx-auto max-w-md rounded-bubble border border-divider bg-surface p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Download size={22} />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-gray-900">
              {mode === 'rustore' ? 'Приложение для Android' : 'Установить приложение'}
            </p>
            <p className="text-sm text-muted">
              {mode === 'rustore'
                ? 'Библия без интернета, напоминания и голосовые заметки. Войдите с той же почтой — всё будет на месте.'
                : mode === 'oneTap'
                  ? 'Откроется с рабочего стола и будет работать без интернета.'
                  : 'Библия будет открываться с главного экрана и работать без интернета.'}
            </p>
          </div>

          <button
            type="button"
            onClick={dismiss}
            aria-label="Скрыть предложение"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted hover:bg-background"
          >
            <X size={20} />
          </button>
        </div>

        {mode === 'rustore' ? (
          <div className="mt-3 flex justify-center">
            <RuStoreBadge height={48} onClick={() => snooze(RUSTORE_DISMISSED_KEY)} />
          </div>
        ) : mode === 'oneTap' ? (
          <Button className="mt-3" fullWidth onClick={() => void install()}>
            Установить
          </Button>
        ) : mode === 'ios' ? (
          <IosSteps />
        ) : (
          <MenuSteps />
        )}
      </div>
    </div>
  )
}
